/**
 * Fine animated film grain.
 *
 * This is not decoration. The table is a very large area of nearly flat
 * plum-black (`palette.felt` 0x1B1030, sitting on `palette.void` 0x0D0A18) with
 * a slow radial gradient across it, and an 8-bit framebuffer cannot represent
 * that gradient without visible banding rings. Grain dithers it away. So the
 * amplitude is tuned against that colour: a quantisation step at this darkness
 * is about 1/255, and the shadow-weighted amplitude below is comfortably above
 * it while still reading as texture rather than noise.
 *
 * Two deliberate choices:
 *
 * - Grain cells are quantised in *device pixels*, so the texture stays the same
 *   apparent size on a retina display instead of dissolving into nothing.
 * - The pattern is advanced on a fixed cadence rather than every frame, which
 *   is what makes it read as film stock instead of sensor noise.
 *
 * Grain is weighted toward the shadows and pulled back in the highlights, so
 * the flat felt gets the dithering it needs while a gold bonus flash stays
 * clean.
 */

import { Filter, GlProgram, defaultFilterVert } from 'pixi.js';

import { GLSL_COLOUR, GLSL_GLOBALS, GLSL_HASH } from './glsl';

const fragment = /* glsl */ `
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform float uTime;
uniform float uAmount;
uniform float uCell;
uniform float uShadowLift;
uniform float uHighlightRolloff;
uniform float uChroma;
uniform float uGrainPeak;
${GLSL_GLOBALS}

${GLSL_COLOUR}
${GLSL_HASH}

void main(void) {
    vec4 src = texture(uTexture, vTextureCoord);
    vec3 rgb = unpremultiply(src);

    // Screen-locked cells: grain is a property of the film, not of the scene,
    // so it is quantised in device pixels rather than in texture space. The
    // wrap keeps the hash away from large coordinates — on hardware that
    // downgrades this shader to mediump, unbounded values collapse the noise
    // into visible stripes. 4096 cells is wider than any real window.
    // gl_FragCoord is relative to the FILTER TARGET, and the target frame
    // tracks the screen shake — so the grid was locked to the shaking scene
    // rather than to the screen, and on a bust a coherent slab of noise slid
    // across the frame. Adding the frame origin back re-anchors it, which is
    // what this shader's own docstring says it does.
    float resScale = uInputPixel.x / max(uInputSize.x, 1e-4);
    vec2 screenPx = gl_FragCoord.xy + uOutputFrame.xy * resScale;

    vec2 cellA = mod(floor(screenPx / max(uCell, 1.0)), 4096.0);
    vec2 cellB = mod(floor(screenPx / max(uCell * 2.6, 1.0)), 4096.0);

    // Two octaves, because real grain CLUMPS and single-octave white noise
    // cannot. One hash per cell is spatially uncorrelated by construction,
    // which is the entire perceptual difference between film grain and digital
    // sensor noise — and at this amplitude it read unambiguously as the latter.
    float mono =
        (hash13(vec3(cellA, uTime)) - 0.5) * 0.62 +
        (hash13(vec3(cellB, uTime + 7.9)) - 0.5) * 0.38;
    vec3 grain = vec3(
        mono,
        mix(mono, hash13(vec3(cellA, uTime + 17.13)) - 0.5, uChroma),
        mix(mono, hash13(vec3(cellA, uTime + 43.71)) - 0.5, uChroma)
    );

    // Two curves summed, not one.
    //
    // The dither ramp (shadows up, highlights down) is a DITHER curve and is
    // correct for what it does — banding lives in the darks. But it is
    // monotonically decreasing, so on its own it made the felt the noisiest
    // thing in frame and the cards, the mid-tone subject, the cleanest. Real
    // photographic granularity peaks in the MID-TONES and falls off at both
    // ends: few developed grains in the toe, saturated overlapping grains in
    // the shoulder. So: keep the dither, and add a mid-tone hump on top.
    float l = luma(rgb);
    float dither = mix(uShadowLift, uHighlightRolloff, smoothstep(0.02, 0.55, l));
    float grainCurve = 4.0 * l * (1.0 - l);
    float weight = uAmount * (dither * 0.55 + grainCurve * uGrainPeak);

    finalColor = premultiply(rgb + grain * weight, src.a);
}
`;

export interface FilmGrainFilterOptions {
  /** Base amplitude, in 0..1 colour units. Keep it low — this is dithering. */
  intensity?: number;
  /** Grain cell size in CSS pixels. Scaled by the device pixel ratio at runtime. */
  cell?: number;
  /** Amplitude multiplier in the deep shadows. */
  shadowLift?: number;
  /** Amplitude multiplier in the highlights. */
  highlightRolloff?: number;
  /** 0 = monochrome grain, 1 = fully decorrelated per channel. */
  chroma?: number;
  /**
   * Weight of the mid-tone granularity hump, on top of the dither ramp.
   *
   * Real film granularity peaks at mid-grey and falls off at both ends; the
   * dither ramp alone is monotonically decreasing, which puts the most noise on
   * the felt and the least on the cards — backwards from film. 0 disables it
   * and restores pure dithering.
   */
  grainPeak?: number;
  /** Pattern changes per second. Lower reads as film, higher as video noise. */
  hz?: number;
}

const DEFAULTS: Required<FilmGrainFilterOptions> = {
  intensity: 0.028,
  cell: 1.25,
  shadowLift: 1.35,
  highlightRolloff: 0.45,
  chroma: 0.28,
  grainPeak: 0.9,
  hz: 30,
};

/**
 * Golden-ratio step, wrapped into a small range. Successive patterns are
 * maximally spread apart, and the value handed to the hash stays small enough
 * to survive a mediump fallback.
 */
const PHI = 1.618033988749;
const TIME_RANGE = 64;

interface GrainUniforms {
  uTime: number;
  uAmount: number;
  uCell: number;
  uShadowLift: number;
  uHighlightRolloff: number;
  uChroma: number;
  uGrainPeak: number;
}

export class FilmGrainFilter extends Filter {
  readonly #uniforms: GrainUniforms;

  #cellCss: number;
  #pixelRatio = 1;
  #hz: number;

  constructor(options: FilmGrainFilterOptions = {}) {
    const o = { ...DEFAULTS, ...options };

    super({
      glProgram: GlProgram.from({
        vertex: defaultFilterVert,
        fragment,
        name: 'wild21-film-grain',
        preferredFragmentPrecision: 'highp',
      }),
      resources: {
        grainUniforms: {
          uTime: { value: 0, type: 'f32' },
          uAmount: { value: o.intensity, type: 'f32' },
          uCell: { value: o.cell, type: 'f32' },
          uShadowLift: { value: o.shadowLift, type: 'f32' },
          uHighlightRolloff: { value: o.highlightRolloff, type: 'f32' },
          uChroma: { value: o.chroma, type: 'f32' },
          uGrainPeak: { value: o.grainPeak, type: 'f32' },
        },
      },
      resolution: 'inherit',
      antialias: 'inherit',
      padding: 0,
    });

    this.#uniforms = this.resources.grainUniforms.uniforms as GrainUniforms;
    this.#cellCss = o.cell;
    this.#hz = o.hz;
    this.pixelRatio = 1;
  }

  /** Base amplitude. */
  get intensity(): number {
    return this.#uniforms.uAmount;
  }
  set intensity(value: number) {
    this.#uniforms.uAmount = Math.max(0, value);
  }

  /** Grain cell size in CSS pixels. */
  get cell(): number {
    return this.#cellCss;
  }
  set cell(value: number) {
    this.#cellCss = Math.max(0.5, value);
    this.#uniforms.uCell = this.#cellCss * this.#pixelRatio;
  }

  /**
   * Device pixel ratio of the canvas. Keeps the grain the same apparent size on
   * a retina display instead of shrinking to invisibility.
   */
  get pixelRatio(): number {
    return this.#pixelRatio;
  }
  set pixelRatio(value: number) {
    this.#pixelRatio = Math.max(0.5, value);
    this.#uniforms.uCell = this.#cellCss * this.#pixelRatio;
  }

  /** Pattern refresh rate, in changes per second. */
  get hz(): number {
    return this.#hz;
  }
  set hz(value: number) {
    this.#hz = Math.max(1, value);
  }

  /** 0 = monochrome, 1 = fully per-channel. */
  get chroma(): number {
    return this.#uniforms.uChroma;
  }
  set chroma(value: number) {
    this.#uniforms.uChroma = Math.min(1, Math.max(0, value));
  }

  /**
   * Advance the pattern. `elapsed` is total seconds since start; the value is
   * quantised so the grain steps on a fixed cadence rather than crawling.
   */
  advance(elapsed: number): void {
    this.#uniforms.uTime = ((Math.floor(elapsed * this.#hz) * PHI) % 1) * TIME_RANGE;
  }
}
